import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from "reactstrap";

const ProductModal = ({ modal, toggleModal, product }) => {
  const [selectedExtras, setSelectedExtras] = useState({});
  const [subtotal, setSubtotal] = useState(parseFloat(product.price) || 0);

  // ✅ Extract Flavors & Sizes
  const flavors =
    product.addons.find((addon) => addon.data_name === "flavors")?.options ||
    [];
  const sizes =
    product.addons.find((addon) => addon.data_name === "extras")?.options || [];

  // ✅ Generate static colors for flavors
  const flavorColors = useMemo(() => {
    const colors = ["#f8d7da", "#d4edda", "#d1ecf1", "#fff3cd", "#f5c6cb"];
    return flavors.map((_, index) => colors[index % colors.length]);
  }, [flavors]);

  // ✅ Handle increasing or decreasing quantity
  const handleExtraChange = (flavorId, sizeId, operation) => {
    setSelectedExtras((prev) => {
      const currentQuantity = prev[flavorId]?.[sizeId] || 0;
      const newQuantity =
        operation === "increase"
          ? currentQuantity + 1
          : Math.max(0, currentQuantity - 1);

      return {
        ...prev,
        [flavorId]: {
          ...prev[flavorId],
          [sizeId]: newQuantity,
        },
      };
    });
  };

  // ✅ Calculate Total Price
  useEffect(() => {
    let total = parseFloat(product.price) || 0;

    Object.keys(selectedExtras).forEach((flavorId) => {
      Object.keys(selectedExtras[flavorId]).forEach((sizeId) => {
        const size = sizes.find((s) => s.id === sizeId);
        if (size) {
          total += parseFloat(size.price) * selectedExtras[flavorId][sizeId];
        }
      });
    });

    setSubtotal(total);
  }, [selectedExtras, sizes, product]);

  // ✅ Collect & Format Selected Addons Data
  const prepareAddonData = () => {
    const formattedAddons = {};

    flavors.forEach((flavor) => {
      const flavorId = flavor.id;
      if (selectedExtras[flavorId]) {
        const selectedSizes = Object.keys(selectedExtras[flavorId])
          .map((sizeId) => {
            const size = sizes.find((s) => s.id === sizeId);
            const quantity = selectedExtras[flavorId][sizeId];
            return size && quantity > 0
              ? `${quantity}x ${size.option} (+$${size.price})`
              : null;
          })
          .filter(Boolean)
          .join(", ");

        if (selectedSizes) {
          formattedAddons[flavor.option] = selectedSizes;
        }
      }
    });
    // console.log(formattedAddons);
    return JSON.stringify(formattedAddons);
  };

  return (
    <Modal isOpen={modal} toggle={toggleModal} size="lg">
      <form method="POST" action="https://tealbo.com/vendor-carts">
        <ModalHeader toggle={toggleModal}>{product.name}</ModalHeader>
        <ModalBody>
          <input type="hidden" name="add-to-cart" value={product.id} />
          <input type="hidden" name="tbo_addons" value={prepareAddonData()} />
          <input type="hidden" name="total_price" value={subtotal.toFixed(2)} />

          <p className="mb-2">
            <strong>Base Price:</strong> $
            {parseFloat(product.price || 0).toFixed(2)}
          </p>

          {/* ✅ Flavors & Sizes in Compact Layout */}
          {flavors.length > 0 ? (
            <div className="flavor-list">
              {flavors.map((flavor, index) => (
                <div
                  key={flavor.id}
                  className="flavor-section p-1 mb-2"
                  style={{
                    borderBottom: "1px solid #ccc", // ✅ Add Border for Separation
                    paddingBottom: "8px",
                  }}
                >
                  <Row className="align-items-center">
                    {/* ✅ Flavor Name */}
                    <Col xs="12" md="6">
                      <div
                        style={{
                          backgroundColor: flavorColors[index],
                          padding: "6px",
                          borderRadius: "5px",
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {flavor.option.replace(/\\/g, "")}
                      </div>
                    </Col>

                    {/* ✅ Sizes Selection */}
                    <Col xs="12" md="6">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        {sizes.map((size) => (
                          <div
                            key={size.id}
                            className={`d-flex justify-content-center align-items-center border rounded ${
                              selectedExtras[flavor.id]?.[size.id]
                                ? "bg-success text-white border-success"
                                : "bg-light text-dark border-secondary"
                            }`}
                            style={{
                              cursor: "pointer",
                              width: "100px", // ✅ Square Box
                              height: "50px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              textAlign: "center",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            onClick={() =>
                              handleExtraChange(flavor.id, size.id, "increase")
                            }
                          >
                            {size.option} (+${size.price})
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          ) : (
            <p>No flavors available</p>
          )}
        </ModalBody>

        <ModalFooter className="d-flex justify-content-between align-items-center">
          <h5>Total: ${subtotal.toFixed(2)}</h5>
          <div>
            <Button type="submit" color="primary" disabled={subtotal <= 0}>
              Add to Cart
            </Button>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ProductModal;
